import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { AlertTriangle, Send } from 'lucide-react';
import {
  Overlay, Modal, ModalTitle, ModalField, ModalList, ModalActions,
  Select, Input, Message, Button, CheckLabel, eventLabel,
} from './ui';

// Reused by the list view (quick re-invite of a known speaker) and the
// detail view (approve & invite after reviewing a researched profile).
// `hasAccount` only changes the confirmation copy - the backend already
// reuses the existing account/profile either way, no AI research is
// triggered by sending an invite.
const InviteModal = ({ open, onClose, onConfirm, busy, events, email, hasAccount, prefillTalkTitle, prefillEventId, prefillRequiresPresentation }) => {
  const intl = useIntl();
  const t = (id, def, values) => intl.formatMessage({ id, defaultMessage: def }, values);
  const [eventId, setEventId] = useState('');
  const [talkTitle, setTalkTitle] = useState('');
  const [requiresPresentation, setRequiresPresentation] = useState(true);

  useEffect(() => {
    if (open) {
      setEventId(prefillEventId ? String(prefillEventId) : '');
      setTalkTitle(prefillTalkTitle || '');
      setRequiresPresentation(prefillRequiresPresentation !== false);
    }
  }, [open, prefillTalkTitle, prefillEventId, prefillRequiresPresentation]);

  if (!open) return null;

  return (
    <Overlay onClick={() => !busy && onClose()}>
      <Modal onClick={e => e.stopPropagation()}>
        <ModalTitle><AlertTriangle size={18} color="#b45309" /><FormattedMessage id="speakerMgmt.confirm.title" defaultMessage="Send speaker invitation?" /></ModalTitle>
        <ModalField>
          <FormattedMessage id="speakerMgmt.confirm.eventLabel" defaultMessage="Which event is this speaker invited to?" />
          <Select value={eventId} onChange={e => setEventId(e.target.value)}>
            <option value="">{t('speakerMgmt.confirm.selectEvent', 'Select an event…')}</option>
            {events.map(event => <option key={event.id} value={event.id}>{eventLabel(event, intl)}</option>)}
          </Select>
        </ModalField>
        <ModalField>
          <FormattedMessage id="speakerMgmt.confirm.talkTitle" defaultMessage="Talk title (optional)" />
          <Input value={talkTitle} onChange={e => setTalkTitle(e.target.value)} />
          <span style={{ fontWeight: 400, fontSize: '.75rem', color: '#64748b' }}><FormattedMessage id="speakerMgmt.confirm.talkTitleHint" defaultMessage="You know the title as organizer — the speaker can only suggest a change later." /></span>
        </ModalField>
        <ModalField>
          <CheckLabel style={{ fontWeight: 600 }}>
            <input type="checkbox" checked={requiresPresentation} onChange={e => setRequiresPresentation(e.target.checked)} />
            <FormattedMessage id="speakerMgmt.confirm.requiresPresentation" defaultMessage="This person needs to submit a presentation" />
          </CheckLabel>
          <span style={{ fontWeight: 400, fontSize: '.75rem', color: '#64748b' }}><FormattedMessage id="speakerMgmt.confirm.requiresPresentationHint" defaultMessage="Uncheck for a moderator or panelist who won't give their own talk — they won't get the presentation reminder emails." /></span>
        </ModalField>
        <FormattedMessage id="speakerMgmt.confirm.intro" defaultMessage="This will:" />
        <ModalList>
          <li><FormattedMessage id="speakerMgmt.confirm.point.email" defaultMessage="Send a real invitation email to {email}" values={{ email: <strong>{email}</strong> }} /></li>
          {hasAccount
            ? <li><FormattedMessage id="speakerMgmt.confirm.point.reinvite" defaultMessage="Add this event to their existing speaker account (no AI research is repeated)" /></li>
            : <li><FormattedMessage id="speakerMgmt.confirm.point.account" defaultMessage="Create or activate a speaker account for this person" /></li>}
          <li><FormattedMessage id="speakerMgmt.confirm.point.results" defaultMessage="Show them the profile values you approved (the AI sources are never shown to the speaker)" /></li>
        </ModalList>
        {!email && <Message error><FormattedMessage id="speakerMgmt.confirm.noEmail" defaultMessage="No email yet — add one in the review form (or retry research) before you can send." /></Message>}
        <ModalActions>
          <Button secondary disabled={busy} onClick={onClose}><FormattedMessage id="speakerMgmt.confirm.cancel" defaultMessage="Cancel" /></Button>
          <Button disabled={busy || !eventId || !email} onClick={() => onConfirm(eventId, talkTitle, requiresPresentation)}><Send size={16} /><FormattedMessage id="speakerMgmt.confirm.send" defaultMessage="Send invitation" /></Button>
        </ModalActions>
      </Modal>
    </Overlay>
  );
};

export default InviteModal;
